import React from "react";
import { Container, Section, PageHero, Card, Eyebrow, CTABand } from "@/components/ui-bits/Primitives";
import { COMMERCIAL_TARGETS, IMAGES } from "@/lib/site-data";
import { TrendingUp, DollarSign, Zap, Users } from "lucide-react";

export default function Commercial() {
    return (
        <div data-testid="commercial-page">
            <PageHero
                kicker="Commercial Property"
                title="EV charging that lifts NOI."
                lead="A turnkey amenity that drives traffic, lengthens dwell time, supports ESG commitments, and protects property value."
                image={IMAGES.commercial}
            />

            <Section>
                <Container>
                    <Eyebrow className="mb-4">Target Properties</Eyebrow>
                    <h2 className="font-heading text-3xl md:text-4xl text-white tracking-tight leading-tight max-w-3xl">Engineered for the way commercial properties operate.</h2>
                    <div className="mt-12 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                        {COMMERCIAL_TARGETS.map((t) => {
                            const I = t.icon;
                            return (
                                <div key={t.label} className="border border-white/10 bg-ink-700/40 p-6 text-center hover:border-electric-400/50 transition-colors">
                                    <I className="w-7 h-7 text-electric-400 mx-auto mb-3" strokeWidth={1.5} />
                                    <div className="text-xs uppercase tracking-[0.16em] text-slate-300">{t.label}</div>
                                </div>
                            );
                        })}
                    </div>
                </Container>
            </Section>

            {/* Static ROI Calculator Mockup */}
            <Section className="border-t border-white/5">
                <Container>
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
                        <div className="lg:col-span-5">
                            <Eyebrow className="mb-4">ROI Visualization</Eyebrow>
                            <h2 className="font-heading text-3xl md:text-4xl text-white tracking-tight leading-tight">A typical site — what the math looks like.</h2>
                            <p className="mt-6 text-slate-400 leading-relaxed">A modeled 6-port DC fast site on a high-traffic commercial property. Actual returns vary with utilization, energy costs, and grant participation.</p>
                            <div className="mt-8 space-y-4">
                                {[
                                    { icon: Zap, k: "6", l: "DC Fast Ports" },
                                    { icon: Users, k: "12,400+", l: "Annual Sessions" },
                                    { icon: TrendingUp, k: "32%", l: "Avg Utilization" },
                                    { icon: DollarSign, k: "Up to $180K", l: "Annual Host Revenue Share" },
                                ].map((s) => {
                                    const I = s.icon;
                                    return (
                                        <div key={s.l} className="flex items-center gap-4 border-l-2 border-electric-400 pl-4 py-2">
                                            <I className="w-5 h-5 text-electric-400" />
                                            <div>
                                                <div className="font-heading text-xl text-white">{s.k}</div>
                                                <div className="text-xs uppercase tracking-[0.18em] text-slate-500">{s.l}</div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                        <div className="lg:col-span-7">
                            <Card hover={false} className="!p-8">
                                <div className="flex items-center justify-between mb-6">
                                    <div className="eyebrow">ROI Projection</div>
                                    <div className="text-xs text-slate-500 font-mono">Static · Illustrative</div>
                                </div>
                                <div className="space-y-5">
                                    {[
                                        { l: "Sessions / day", v: 34, max: 80 },
                                        { l: "Avg kWh / session", v: 28, max: 60 },
                                        { l: "Revenue / kWh", v: 0.48, max: 0.6, prefix: "$" },
                                        { l: "Utilization", v: 32, max: 100, suffix: "%" },
                                    ].map((r) => (
                                        <div key={r.l}>
                                            <div className="flex justify-between text-xs mb-2"><span className="uppercase tracking-[0.18em] text-slate-400">{r.l}</span><span className="text-electric-400 font-mono">{r.prefix||""}{r.v}{r.suffix||""}</span></div>
                                            <div className="h-1.5 bg-white/5 relative overflow-hidden">
                                                <div className="h-full bg-electric-400" style={{ width: `${(r.v / r.max) * 100}%` }} />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                <div className="mt-8 pt-6 border-t border-white/10 grid grid-cols-3 gap-4">
                                    <div><div className="text-xs uppercase tracking-[0.18em] text-slate-500">Year 1</div><div className="font-heading text-2xl text-white mt-1">$148K</div></div>
                                    <div><div className="text-xs uppercase tracking-[0.18em] text-slate-500">Year 3</div><div className="font-heading text-2xl text-white mt-1">$520K</div></div>
                                    <div><div className="text-xs uppercase tracking-[0.18em] text-slate-500">Year 5</div><div className="font-heading text-2xl text-electric-400 mt-1">$960K</div></div>
                                </div>
                                <p className="mt-6 text-[11px] text-slate-500 leading-relaxed">Illustrative model only — your actual project economics depend on site utilization, energy procurement, grant capture, and revenue-share structure.</p>
                            </Card>
                        </div>
                    </div>
                </Container>
            </Section>

            <CTABand title="Run the numbers for your property." subtitle="Send us the address and parking count — we'll build the model." />
        </div>
    );
}
